// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';

/**
 * Reusable planet icon component - procedurally generated planet
 * @param {number} size - Width/height in pixels (default: 300)
 * @param {string} primaryColor - Primary planet color (default: #4488ff)
 * @param {string} secondaryColor - Secondary/atmosphere color (default: #6699ff)
 * @param {boolean} hasAtmosphere - Show atmosphere glow (default: true)
 * @param {boolean} hasRings - Show planetary rings (default: false)
 * @param {object} style - Additional inline styles
 */
const PlanetIcon = ({ 
  size = 300, 
  primaryColor = '#4488ff',
  secondaryColor = '#6699ff',
  hasAtmosphere = true,
  hasRings = false,
  style = {}
}) => {
  // Generate slightly darker shade for surface details
  const darkerShade = adjustBrightness(primaryColor, -20);
  const lighterShade = adjustBrightness(primaryColor, 20);
  
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 300 300" 
      style={{ 
        filter: hasAtmosphere ? `drop-shadow(0 0 30px ${secondaryColor})` : 'none',
        ...style
      }}
    >
      <defs>
        {/* Clipping path for planet surface */}
        <clipPath id={`planetClip-${size}`}>
          <circle cx="150" cy="150" r="120" />
        </clipPath>
        
        {/* Planet surface gradient */}
        <radialGradient id={`planetGradient-${size}`} cx="35%" cy="35%">
          <stop offset="0%" stopColor={lighterShade} />
          <stop offset="50%" stopColor={primaryColor} />
          <stop offset="100%" stopColor={darkerShade} />
        </radialGradient>
        
        {/* Atmosphere gradient */}
        {hasAtmosphere && (
          <radialGradient id={`atmosphereGradient-${size}`} cx="50%" cy="50%">
            <stop offset="70%" stopColor={secondaryColor} stopOpacity="0" />
            <stop offset="85%" stopColor={secondaryColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={secondaryColor} stopOpacity="0.6" />
          </radialGradient>
        )}
        
        {/* Cloud pattern */}
        <filter id={`clouds-${size}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="3" seed="2" />
          <feColorMatrix type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncA type="discrete" tableValues="0 0 0 1 1" />
          </feComponentTransfer>
        </filter>
      </defs>
      
      {/* Planetary rings (behind planet) */}
      {hasRings && (
        <g opacity="0.6">
          <ellipse 
            cx="150" 
            cy="150" 
            rx="200" 
            ry="40" 
            fill="none" 
            stroke={adjustBrightness(primaryColor, -30)} 
            strokeWidth="8"
            opacity="0.4"
          />
          <ellipse 
            cx="150" 
            cy="150" 
            rx="220" 
            ry="44" 
            fill="none" 
            stroke={adjustBrightness(primaryColor, -20)} 
            strokeWidth="6"
            opacity="0.3"
          />
        </g>
      )}
      
      {/* Planet surface */}
      <circle 
        cx="150" 
        cy="150" 
        r="120" 
        fill={`url(#planetGradient-${size})`}
      />
      
      {/* Surface details - continents/terrain (clipped to planet) */}
      <g clipPath={`url(#planetClip-${size})`}>
        <g opacity="0.25">
          <ellipse cx="120" cy="130" rx="35" ry="25" fill={darkerShade} />
          <ellipse cx="170" cy="160" rx="30" ry="20" fill={darkerShade} />
          <ellipse cx="140" cy="180" rx="25" ry="18" fill={darkerShade} />
          <ellipse cx="180" cy="140" rx="20" ry="30" fill={darkerShade} />
        </g>
      </g>
      
      {/* Cloud layer (clipped to planet) */}
      <circle 
        cx="150" 
        cy="150" 
        r="120" 
        fill="white" 
        opacity="0.12"
        filter={`url(#clouds-${size})`}
        clipPath={`url(#planetClip-${size})`}
      />
      
      {/* Atmosphere glow */}
      {hasAtmosphere && (
        <circle 
          cx="150" 
          cy="150" 
          r="135" 
          fill={`url(#atmosphereGradient-${size})`}
        />
      )}
      
      {/* Planetary rings (in front of planet) */}
      {hasRings && (
        <g opacity="0.8">
          <ellipse 
            cx="150" 
            cy="150" 
            rx="200" 
            ry="40" 
            fill="none" 
            stroke={primaryColor} 
            strokeWidth="10"
            opacity="0.5"
          />
          <ellipse 
            cx="150" 
            cy="150" 
            rx="220" 
            ry="44" 
            fill="none" 
            stroke={lighterShade} 
            strokeWidth="8"
            opacity="0.4"
          />
        </g>
      )}
      
      {/* Subtle terminator shadow (day/night) - anchored to right edge */}
      <ellipse 
        cx="210" 
        cy="150" 
        rx="70" 
        ry="120" 
        fill="black" 
        opacity="0.25"
        clipPath={`url(#planetClip-${size})`}
      />
    </svg>
  );
};

/**
 * Helper function to adjust color brightness
 */
function adjustBrightness(color, amount) {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.slice(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.slice(2, 4), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.slice(4, 6), 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Get planet color based on system characteristics
 * Uses config data if available, otherwise falls back to pattern matching
 */
export function getPlanetColors(location) {
  // If colors are defined in the location data, use them
  if (location.primaryColor && location.secondaryColor) {
    return {
      primary: location.primaryColor,
      secondary: location.secondaryColor,
      hasRings: location.hasRings || false
    };
  }
  
  // Fallback to pattern matching for backwards compatibility
  const name = location.name.toUpperCase();
  const resources = location.resources.toUpperCase();
  const threat = location.threat.toUpperCase();
  
  // Special cases
  if (name.includes('SOL') || name.includes('EARTH')) {
    return { primary: '#4488ff', secondary: '#6699ff', hasRings: false }; // Blue Earth
  }
  
  if (name.includes('BETELGEUSE')) {
    return { primary: '#ff6644', secondary: '#ff8866', hasRings: false }; // Red supergiant
  }
  
  if (name.includes('RIGEL')) {
    return { primary: '#aaccff', secondary: '#ccddff', hasRings: false }; // Blue supergiant
  }
  
  if (name.includes('TRAPPIST')) {
    return { primary: '#cc6644', secondary: '#dd8866', hasRings: false }; // Red dwarf system
  }
  
  if (name.includes('POLARIS')) {
    return { primary: '#ffffaa', secondary: '#ffffcc', hasRings: false }; // Yellow supergiant
  }
  
  // Resource-based colors
  if (resources.includes('VERY HIGH')) {
    return { primary: '#44ff88', secondary: '#66ffaa', hasRings: true }; // Rich green with rings
  }
  
  if (resources.includes('HIGH')) {
    return { primary: '#88cc44', secondary: '#aadd66', hasRings: false }; // Green
  }
  
  if (resources.includes('MEDIUM')) {
    return { primary: '#ffaa44', secondary: '#ffcc66', hasRings: false }; // Orange
  }
  
  if (resources.includes('LOW')) {
    return { primary: '#cc8844', secondary: '#ddaa66', hasRings: false }; // Brown
  }
  
  // Threat-based colors
  if (threat.includes('HIGH')) {
    return { primary: '#ff4444', secondary: '#ff6666', hasRings: false }; // Red
  }
  
  if (threat.includes('UNKNOWN')) {
    return { primary: '#aa44ff', secondary: '#cc66ff', hasRings: true }; // Purple with rings
  }
  
  // Default blue planet
  return { primary: '#4488ff', secondary: '#6699ff', hasRings: false };
}

export default PlanetIcon;
